import type { ReactNode } from 'react';

import { EmptyState } from './empty-state';
import { InlineError } from './inline-error';
import { Skeleton } from './skeleton';

const defaultSkeleton = (
  <div className="gap-snug flex flex-col">
    <Skeleton className="h-8" />
    <Skeleton className="h-8" />
    <Skeleton className="h-8" />
  </div>
);

/**
 * 목록·상세의 로딩 → 실패 → 빈 상태 → 콘텐츠 순서를 고정한다 — E14 §13.
 * `error`는 해석된 메시지다. 문자열이면 `InlineError`로, 그 외 노드는 그대로 렌더한다.
 * `children`을 함수로 주면 pending·error·empty 동안 데이터를 읽지 않는다.
 */
export interface QueryStateProps {
  pending: boolean;
  error?: ReactNode;
  onRetry?: () => void;
  empty?: boolean;
  emptyMessage?: ReactNode;
  emptyAction?: ReactNode;
  skeleton?: ReactNode;
  children: ReactNode | (() => ReactNode);
}

export function QueryState({
  pending,
  error,
  onRetry,
  empty,
  emptyMessage,
  emptyAction,
  skeleton,
  children,
}: QueryStateProps) {
  if (pending) {
    return <>{skeleton ?? defaultSkeleton}</>;
  }

  if (error) {
    if (typeof error === 'string') {
      return <InlineError onRetry={onRetry}>{error}</InlineError>;
    }
    return <>{error}</>;
  }

  if (empty) {
    return <EmptyState action={emptyAction}>{emptyMessage}</EmptyState>;
  }

  return <>{typeof children === 'function' ? children() : children}</>;
}
